<template>
    <div class="legacy-bulk-product-information">
        <BAlert
            show
            variant="warning"
            class="app-alert--attention"
        >
            <div class="alert-body d-flex align-items-center">
                <feather-icon
                    icon="InfoIcon"
                    class="mr-1 flex-shrink-0"
                />
                <span>
                    {{ $t('Page(ReceivedRequest):Component(GeneralProductInformation):bulk-update-notice') }}
                </span>
            </div>
        </BAlert>

        <BAlert
            v-if="error"
            show
            variant="danger"
        >
            <div class="alert-body d-flex align-items-center justify-content-between">
                <span>{{ $t('Page(ReceivedRequests):Component(BulkReply):load-error') }}</span>
                <BButton
                    size="sm"
                    variant="outline-danger"
                    @click="$emit('retry')"
                >
                    {{ $t('Component(Button):retry') }}
                </BButton>
            </div>
        </BAlert>

        <Overlay :show="loading">
            <HamletStaticTable
                :columns="columns"
                :items="products"
                table-container-class="table-responsive hamlet-table legacy-bulk-products-table"
            >
                <template #header>
                    <div class="card-header">
                        <h4 class="mb-0">
                            {{ $t('Page(ReceivedRequest):Component(GeneralProductInformation):included-products') }}
                        </h4>
                    </div>
                </template>

                <template #cell-name="{ item }">
                    <div class="d-flex align-items-center">
                        <span>{{ item.name || '—' }}</span>
                        <BBadge
                            v-if="item.isPrimary"
                            variant="light-primary"
                            class="ml-1"
                        >
                            {{ $t('Page(ReceivedRequest):Component(GeneralProductInformation):primary-request') }}
                        </BBadge>
                    </div>
                </template>

                <template #cell-identifier="{ item }">
                    {{ item.identifier || '—' }}
                </template>

                <template #cell-actions="{ item }">
                    <BButton
                        v-if="!item.isPrimary"
                        size="sm"
                        variant="outline-danger"
                        :disabled="busy || loading"
                        @click="$emit('remove', item.requestId)"
                    >
                        {{ $t('Component(Button):remove') }}
                    </BButton>
                </template>
            </HamletStaticTable>
        </Overlay>
    </div>
</template>

<script>
import { BAlert, BBadge, BButton } from 'bootstrap-vue'
import HamletStaticTable from '@core/components/common/HamletStaticTableV1.vue'
import Overlay from '@core/components/common/Overlay.vue'
import {
    getNameOrId,
    ProductIdentifiers,
    ProductNames,
} from '@/libs/product'

// Intentional legacy copy. Delete it together with the legacy flows after manifest migration.
export default {
    name: 'LegacyBulkProductInformation',
    components: {
        BAlert,
        BBadge,
        BButton,
        HamletStaticTable,
        Overlay,
    },
    props: {
        requestId: { type: [Number, String], required: true },
        productId: { type: [Number, String], default: null },
        request: { type: Object, required: true },
        product: { type: Object, required: true },
        candidates: { type: Array, default: () => [] },
        selectedRequestIds: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        busy: { type: Boolean, default: false },
        error: { type: [Error, String], default: null },
    },
    data() {
        return {
            columns: [
                {
                    key: 'name',
                    label: this.$t('Component(Label):product-name'),
                },
                {
                    key: 'identifier',
                    label: this.$t('Component(Label):product-id'),
                },
                {
                    key: 'actions',
                    label: '',
                    thClass: 'legacy-bulk-products-actions',
                    tdClass: 'legacy-bulk-products-actions',
                },
            ],
        }
    },
    computed: {
        products() {
            return [
                this.primaryProduct,
                ...this.selectedRequestIds
                    .map(requestId => this.candidates.find(
                        candidate => Number(candidate.requestId) === Number(requestId),
                    ))
                    .filter(Boolean)
                    .map(candidate => ({
                        id: `request-${candidate.requestId}`,
                        requestId: candidate.requestId,
                        productId: candidate.product && candidate.product.id,
                        name: candidate.product && candidate.product.name,
                        identifier: candidate.product && candidate.product.identifier,
                        isPrimary: false,
                    })),
            ]
        },
        primaryProduct() {
            const customerProduct = this.request.customerProduct || {}

            return {
                id: `request-${this.requestId}`,
                requestId: this.requestId,
                productId: this.productId,
                name: customerProduct.name
                    || getNameOrId(this.product.names || [], ProductNames.OUR_PRODUCT_NAME)
                    || this.product.title
                    || this.product.name
                    || '',
                identifier: customerProduct.identifier
                    || getNameOrId(
                        this.product.identifiers || [],
                        ProductIdentifiers.PRODUCT_ID,
                    )
                    || '',
                isPrimary: true,
            }
        },
        isValid() {
            return !this.loading && !this.error && this.selectedRequestIds.length > 0
        },
    },
    watch: {
        isValid: {
            immediate: true,
            handler(value) {
                this.$emit('validityChanged', value)
            },
        },
    },
}
</script>

<style lang="scss">
.legacy-bulk-products-table table th,
.legacy-bulk-products-table table td {
    padding: 0.72rem 1rem;
    vertical-align: middle;
}

.legacy-bulk-products-actions {
    width: 1%;
    text-align: right;
    white-space: nowrap;
}
</style>
